import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  ScrollView,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { ThemedInput } from "@/components/ThemedInput";
import { EntityIcon } from "@/components/ui/EntityIcon";
import { TagFilter } from "@/components/tags/TagFilter";
import { useThemeColor } from "@/hooks/useThemeColor";
import { MemoryModel, MediaModel, Contact, ContactModel, GroupModel } from "common247";
import {
  applyMediaFilter,
  IMediaFilterCriteria,
  IFilteredMediaResultSet,
} from "@/services/media-filter-service";

export interface IMediaFilterProps {
  /** Optional custom header title (defaults to "Filter Media") */
  title?: string;
  /** Source pool of media items */
  allMedia: MediaModel[];
  /** Source pool of memories */
  allMemories?: MemoryModel[];
  /** Source pool of contacts */
  allContacts?: (Contact | ContactModel)[];
  /** Source pool of groups */
  allGroups?: GroupModel[];
  /** Callback triggered whenever filters change with the computed result set */
  onFilteredResultsChange: (results: IFilteredMediaResultSet) => void;
  /** Optional container style override */
  style?: StyleProp<ViewStyle>;
  /** Initial filter criteria */
  initialCriteria?: IMediaFilterCriteria;
}

const EMPTY_MEDIA: MediaModel[] = [];
const EMPTY_MEMORIES: MemoryModel[] = [];
const EMPTY_CONTACTS: (Contact | ContactModel)[] = [];
const EMPTY_GROUPS: GroupModel[] = [];

/**
 * Standardized Archival Heirloom Media Filter component.
 * Filters across Media items by text search, date range, media types, categories,
 * favourite rating, tags, memories, contacts, and groups.
 */
export function MediaFilter({
  title = "Filter Media",
  allMedia = EMPTY_MEDIA,
  allMemories = EMPTY_MEMORIES,
  allContacts = EMPTY_CONTACTS,
  allGroups = EMPTY_GROUPS,
  onFilteredResultsChange,
  style,
  initialCriteria,
}: IMediaFilterProps): React.JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState(initialCriteria?.searchQuery || "");
  const [dateFrom, setDateFrom] = useState<string>(
    initialCriteria?.dateFrom ? String(initialCriteria.dateFrom) : ""
  );
  const [dateTo, setDateTo] = useState<string>(
    initialCriteria?.dateTo ? String(initialCriteria.dateTo) : ""
  );
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(
    initialCriteria?.tagIds || []
  );
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>(
    initialCriteria?.contactIds || []
  );
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>(
    initialCriteria?.groupIds || []
  );
  const [selectedMemoryIds, setSelectedMemoryIds] = useState<string[]>(
    initialCriteria?.memoryIds || []
  );
  const [selectedMediaType, setSelectedMediaType] = useState<string>(
    initialCriteria?.mediaType || "all"
  );
  const [selectedCategory, setSelectedCategory] = useState<string>(
    initialCriteria?.mediaCategory || "all"
  );
  const [minRating, setMinRating] = useState<number>(initialCriteria?.minRating || 1);

  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");
  const badgeBg = useThemeColor({}, "buttonSecondary");
  const textMuted = useThemeColor({}, "textMuted");

  const onFilteredResultsChangeRef = useRef(onFilteredResultsChange);
  onFilteredResultsChangeRef.current = onFilteredResultsChange;
  const lastFingerprintRef = useRef<string>("");

  const criteria: IMediaFilterCriteria = useMemo(
    () => ({
      searchQuery: searchQuery.trim() || undefined,
      dateFrom: dateFrom.trim() || null,
      dateTo: dateTo.trim() || null,
      tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
      contactIds: selectedContactIds.length > 0 ? selectedContactIds : undefined,
      groupIds: selectedGroupIds.length > 0 ? selectedGroupIds : undefined,
      memoryIds: selectedMemoryIds.length > 0 ? selectedMemoryIds : undefined,
      mediaType: selectedMediaType !== "all" ? selectedMediaType : undefined,
      mediaCategory: selectedCategory !== "all" ? selectedCategory : undefined,
      minRating: minRating > 1 ? minRating : undefined,
    }),
    [
      searchQuery,
      dateFrom,
      dateTo,
      selectedTagIds,
      selectedContactIds,
      selectedGroupIds,
      selectedMemoryIds,
      selectedMediaType,
      selectedCategory,
      minRating,
    ]
  );

  const filteredResultSet: IFilteredMediaResultSet = useMemo(() => {
    return applyMediaFilter(
      allMedia,
      allMemories,
      allContacts,
      allGroups,
      criteria
    );
  }, [allMedia, allMemories, allContacts, allGroups, criteria]);

  useEffect(() => {
    const fingerprint = JSON.stringify({
      criteria,
      countMedia: filteredResultSet.media.length,
      countMemories: filteredResultSet.memories.length,
    });
    if (fingerprint !== lastFingerprintRef.current) {
      lastFingerprintRef.current = fingerprint;
      onFilteredResultsChangeRef.current(filteredResultSet);
    }
  }, [filteredResultSet, criteria]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchQuery.trim()) count++;
    if (dateFrom.trim()) count++;
    if (dateTo.trim()) count++;
    if (selectedTagIds.length > 0) count += selectedTagIds.length;
    if (selectedContactIds.length > 0) count += selectedContactIds.length;
    if (selectedGroupIds.length > 0) count += selectedGroupIds.length;
    if (selectedMemoryIds.length > 0) count += selectedMemoryIds.length;
    if (selectedMediaType !== "all") count++;
    if (selectedCategory !== "all") count++;
    if (minRating > 1) count++;
    return count;
  }, [
    searchQuery,
    dateFrom,
    dateTo,
    selectedTagIds,
    selectedContactIds,
    selectedGroupIds,
    selectedMemoryIds,
    selectedMediaType,
    selectedCategory,
    minRating,
  ]);

  const handleResetFilters = (): void => {
    setSearchQuery("");
    setDateFrom("");
    setDateTo("");
    setSelectedTagIds([]);
    setSelectedContactIds([]);
    setSelectedGroupIds([]);
    setSelectedMemoryIds([]);
    setSelectedMediaType("all");
    setSelectedCategory("all");
    setMinRating(1);
  };

  const toggleContact = (id: string): void => {
    setSelectedContactIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const toggleGroup = (id: string): void => {
    setSelectedGroupIds((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]
    );
  };

  const toggleMemory = (id: string): void => {
    setSelectedMemoryIds((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );
  };

  return (
    <View style={[styles.container, { backgroundColor: cardBg, borderColor }, style]}>
      {/* Summary Header & Toggle Bar */}
      <View style={styles.headerBar}>
        <TouchableOpacity
          style={styles.summaryRow}
          onPress={() => setIsExpanded(!isExpanded)}
          accessibilityRole="button"
          accessibilityLabel="Toggle media filter options"
        >
          <View style={styles.titleWithBadge}>
            <Ionicons name="filter-outline" size={16} color={tintColor} />
            <ThemedText style={styles.headerTitle}>{title}</ThemedText>
            {activeFilterCount > 0 && (
              <View style={[styles.activeBadge, { backgroundColor: tintColor }]}>
                <ThemedText style={styles.activeBadgeText}>{activeFilterCount}</ThemedText>
              </View>
            )}
          </View>

          {/* Counts preview */}
          <View style={styles.countsRow}>
            <View style={[styles.countChip, { backgroundColor: badgeBg, borderColor }]}>
              <EntityIcon type="media" size={12} color={tintColor} style={{ marginRight: 3 }} />
              <ThemedText style={styles.countText}>{filteredResultSet.totalCounts.media}</ThemedText>
            </View>

            <View style={[styles.countChip, { backgroundColor: badgeBg, borderColor }]}>
              <EntityIcon type="memory" size={12} color={tintColor} style={{ marginRight: 3 }} />
              <ThemedText style={styles.countText}>
                {filteredResultSet.totalCounts.memories}
              </ThemedText>
            </View>

            <View style={[styles.countChip, { backgroundColor: badgeBg, borderColor }]}>
              <EntityIcon type="contact" size={12} color={tintColor} style={{ marginRight: 3 }} />
              <ThemedText style={styles.countText}>
                {filteredResultSet.totalCounts.contacts}
              </ThemedText>
            </View>

            <View style={[styles.countChip, { backgroundColor: badgeBg, borderColor }]}>
              <EntityIcon type="group" size={12} color={tintColor} style={{ marginRight: 3 }} />
              <ThemedText style={styles.countText}>
                {filteredResultSet.totalCounts.groups}
              </ThemedText>
            </View>
          </View>

          <Ionicons
            name={isExpanded ? "chevron-up" : "chevron-down"}
            size={18}
            color={tintColor}
            style={{ marginLeft: 6 }}
          />
        </TouchableOpacity>
      </View>

      {/* Expanded Filter Panel */}
      {isExpanded && (
        <View style={[styles.filterPanel, { borderTopColor: borderColor }]}>
          {/* Row 1: Search Query */}
          <View style={styles.filterRow}>
            <ThemedInput
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholder="Search by title, description, filename, hash, or tags..."
              style={styles.inputField}
            />
          </View>

          {/* Row 2: Date From and Date To */}
          <View style={styles.dateRow}>
            <View style={styles.dateCol}>
              <ThemedText style={[styles.fieldLabel, { color: textMuted }]}>From Date</ThemedText>
              <ThemedInput
                value={dateFrom}
                onChangeText={setDateFrom}
                placeholder="e.g. 2026, Jan 2026, 01-Jan-2026"
                style={styles.inputField}
              />
            </View>
            <View style={styles.dateCol}>
              <ThemedText style={[styles.fieldLabel, { color: textMuted }]}>To Date</ThemedText>
              <ThemedInput
                value={dateTo}
                onChangeText={setDateTo}
                placeholder="e.g. 2026, Dec 2026, 31-Dec-2026"
                style={styles.inputField}
              />
            </View>
          </View>

          {/* Row 3: Media Type & Minimum Rating */}
          <View style={styles.optionsRow}>
            <View style={styles.optionCol}>
              <ThemedText style={[styles.fieldLabel, { color: textMuted }]}>Media Type</ThemedText>
              <View style={styles.chipRow}>
                {["all", "image", "video", "audio", "document", "pdf"].map((t) => (
                  <TouchableOpacity
                    key={t}
                    style={[
                      styles.choiceChip,
                      { backgroundColor: badgeBg, borderColor },
                      selectedMediaType === t && { borderColor: tintColor, backgroundColor: "#D0E1F9" },
                    ]}
                    onPress={() => setSelectedMediaType(t)}
                  >
                    <ThemedText
                      style={[
                        styles.choiceChipText,
                        selectedMediaType === t && { color: "#1E3A8A", fontWeight: "700" },
                      ]}
                    >
                      {t.toUpperCase()}
                    </ThemedText>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.optionCol}>
              <ThemedText style={[styles.fieldLabel, { color: textMuted }]}>Min Rating</ThemedText>
              <View style={styles.chipRow}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <TouchableOpacity
                    key={star}
                    style={[
                      styles.choiceChip,
                      { backgroundColor: badgeBg, borderColor },
                      minRating === star && { borderColor: tintColor, backgroundColor: "#D0E1F9" },
                    ]}
                    onPress={() => setMinRating(star)}
                  >
                    <ThemedText
                      style={[
                        styles.choiceChipText,
                        minRating === star && { color: "#1E3A8A", fontWeight: "700" },
                      ]}
                    >
                      {star === 1 ? "ALL" : `${star}★`}
                    </ThemedText>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </View>

          {/* Row 4: Media Category */}
          <View style={styles.categorySection}>
            <ThemedText style={[styles.fieldLabel, { color: textMuted }]}>Media Category</ThemedText>
            <View style={styles.chipRow}>
              {[
                { key: "all", label: "ALL CATEGORIES" },
                { key: "standard", label: "STANDARD" },
                { key: "generated", label: "GENERATED" },
                { key: "documents", label: "DOCUMENTS" },
                { key: "emoji", label: "EMOJI" },
                { key: "sticker", label: "STICKER" },
              ].map((cat) => (
                <TouchableOpacity
                  key={cat.key}
                  style={[
                    styles.choiceChip,
                    { backgroundColor: badgeBg, borderColor },
                    selectedCategory === cat.key && { borderColor: tintColor, backgroundColor: "#D0E1F9" },
                  ]}
                  onPress={() => setSelectedCategory(cat.key)}
                >
                  <ThemedText
                    style={[
                      styles.choiceChipText,
                      selectedCategory === cat.key && { color: "#1E3A8A", fontWeight: "700" },
                    ]}
                  >
                    {cat.label}
                  </ThemedText>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Row 5: Associated Memories */}
          {allMemories.length > 0 && (
            <View style={styles.entityFilterSection}>
              <ThemedText style={[styles.fieldLabel, { color: textMuted }]}>
                Filter by Memory ({selectedMemoryIds.length} selected)
              </ThemedText>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.chipsScroll}>
                {allMemories.map((mem) => {
                  const isSelected = selectedMemoryIds.includes(mem.id!);
                  return (
                    <TouchableOpacity
                      key={mem.id}
                      style={[
                        styles.entityChip,
                        { backgroundColor: badgeBg, borderColor },
                        isSelected && { borderColor: tintColor, backgroundColor: "#D0E1F9" },
                      ]}
                      onPress={() => toggleMemory(mem.id!)}
                    >
                      <EntityIcon type="memory" size={12} color={tintColor} style={{ marginRight: 4 }} />
                      <ThemedText
                        style={[
                          styles.entityChipText,
                          isSelected && { color: "#1E3A8A", fontWeight: "700" },
                        ]}
                        numberOfLines={1}
                      >
                        {mem.title || "Memory"}
                      </ThemedText>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>
          )}

          {/* Row 6: Filter by Contacts */}
          {allContacts.length > 0 && (
            <View style={styles.entityFilterSection}>
              <ThemedText style={[styles.fieldLabel, { color: textMuted }]}>
                Filter by Contacts ({selectedContactIds.length} selected)
              </ThemedText>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.chipsScroll}>
                {allContacts.map((c) => {
                  const isSelected = selectedContactIds.includes(c.id!);
                  return (
                    <TouchableOpacity
                      key={c.id}
                      style={[
                        styles.entityChip,
                        { backgroundColor: badgeBg, borderColor },
                        isSelected && { borderColor: tintColor, backgroundColor: "#D0E1F9" },
                      ]}
                      onPress={() => toggleContact(c.id!)}
                    >
                      <EntityIcon type="contact" size={12} color={tintColor} style={{ marginRight: 4 }} />
                      <ThemedText
                        style={[
                          styles.entityChipText,
                          isSelected && { color: "#1E3A8A", fontWeight: "700" },
                        ]}
                        numberOfLines={1}
                      >
                        {c.displayName || c.email}
                      </ThemedText>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>
          )}

          {/* Row 7: Filter by Groups */}
          {allGroups.length > 0 && (
            <View style={styles.entityFilterSection}>
              <ThemedText style={[styles.fieldLabel, { color: textMuted }]}>
                Filter by Groups ({selectedGroupIds.length} selected)
              </ThemedText>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.chipsScroll}>
                {allGroups.map((g) => {
                  const isSelected = selectedGroupIds.includes(g.id!);
                  return (
                    <TouchableOpacity
                      key={g.id}
                      style={[
                        styles.entityChip,
                        { backgroundColor: badgeBg, borderColor },
                        isSelected && { borderColor: tintColor, backgroundColor: "#D0E1F9" },
                      ]}
                      onPress={() => toggleGroup(g.id!)}
                    >
                      <EntityIcon type="group" size={12} color={tintColor} style={{ marginRight: 4 }} />
                      <ThemedText
                        style={[
                          styles.entityChipText,
                          isSelected && { color: "#1E3A8A", fontWeight: "700" },
                        ]}
                        numberOfLines={1}
                      >
                        {g.name}
                      </ThemedText>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>
          )}

          {/* Row 8: Tag Filter */}
          <View style={{ paddingHorizontal: 10, paddingVertical: 4 }}>
            <TagFilter
              selectedTagIds={selectedTagIds}
              onSelectedTagsChange={setSelectedTagIds}
            />
          </View>

          {/* Bottom Controls */}
          {activeFilterCount > 0 && (
            <View style={styles.footerRow}>
              <TouchableOpacity
                onPress={handleResetFilters}
                style={[styles.resetButton, { borderColor }]}
                accessibilityRole="button"
                accessibilityLabel="Clear all media filters"
              >
                <Ionicons name="close-circle-outline" size={14} color="#EF4444" style={{ marginRight: 4 }} />
                <ThemedText style={styles.resetText}>Clear All Filters</ThemedText>
              </TouchableOpacity>
            </View>
          )}
        </View>
      )}
    </View>
  );
}

export default MediaFilter;

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderRadius: 0,
    marginBottom: 10,
    width: "100%",
  },
  headerBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  summaryRow: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  titleWithBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginRight: 10,
  },
  headerTitle: {
    fontSize: 13,
    fontWeight: "700",
  },
  activeBadge: {
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 0,
  },
  activeBadgeText: {
    color: "#FFFFFF",
    fontSize: 10,
    fontWeight: "bold",
  },
  countsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flex: 1,
    justifyContent: "flex-end",
    marginRight: 6,
  },
  countChip: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderWidth: 1,
    borderRadius: 0,
  },
  countText: {
    fontSize: 11,
    fontWeight: "600",
  },
  filterPanel: {
    borderTopWidth: 1,
    paddingTop: 8,
    paddingBottom: 10,
    gap: 8,
  },
  filterRow: {
    paddingHorizontal: 10,
  },
  inputField: {
    height: 36,
    fontSize: 13,
  },
  dateRow: {
    flexDirection: "row",
    gap: 8,
    paddingHorizontal: 10,
  },
  dateCol: {
    flex: 1,
    gap: 4,
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  optionsRow: {
    flexDirection: "row",
    gap: 12,
    paddingHorizontal: 10,
  },
  optionCol: {
    flex: 1,
    gap: 4,
  },
  categorySection: {
    paddingHorizontal: 10,
    gap: 4,
  },
  chipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
  choiceChip: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderWidth: 1,
    borderRadius: 0,
  },
  choiceChipText: {
    fontSize: 10,
    fontWeight: "600",
  },
  entityFilterSection: {
    paddingHorizontal: 10,
    gap: 4,
  },
  chipsScroll: {
    flexDirection: "row",
  },
  entityChip: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderWidth: 1,
    borderRadius: 0,
    marginRight: 6,
  },
  entityChipText: {
    fontSize: 11,
    maxWidth: 120,
  },
  footerRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    paddingHorizontal: 10,
    paddingTop: 4,
  },
  resetButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderWidth: 1,
    borderRadius: 0,
  },
  resetText: {
    color: "#EF4444",
    fontSize: 11,
    fontWeight: "600",
  },
});
